<template>
  <Teleport to="body">
    <Transition name="sa-fade">
      <div v-if="sessionAdminOpen" class="sa-overlay" @mousedown.self="close">
        <div class="sa-box" role="dialog" aria-label="Sessões dos agentes">
          <div class="sa-head">
            <span class="sa-head-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </span>
            <div class="sa-head-text">
              <strong>Sessões dos agentes</strong>
              <span>Revogue sessões e desconecte agentes na hora</span>
            </div>
            <kbd class="sa-kbd">Ctrl Shift S</kbd>
            <button type="button" class="sa-icon-btn" title="Atualizar a lista" :disabled="loading" @click="load">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            </button>
            <button type="button" class="sa-icon-btn" title="Fechar (Esc)" @click="close">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <div class="sa-search-row">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input ref="searchRef" v-model="query" class="sa-search" placeholder="Buscar agente pelo nome ou e-mail..." autocomplete="off" spellcheck="false" />
            <div class="sa-filters" role="group" aria-label="Filtro">
              <button type="button" :class="{ 'is-on': !onlyOnline }" @click="onlyOnline = false">Todos <b>{{ agents.length }}</b></button>
              <button type="button" :class="{ 'is-on': onlyOnline }" @click="onlyOnline = true">Online <b>{{ onlineCount }}</b></button>
            </div>
          </div>

          <div class="sa-selectbar">
            <label class="sa-check">
              <input type="checkbox" :checked="allVisibleSelected" :indeterminate.prop="someVisibleSelected && !allVisibleSelected" :disabled="!selectable.length" @change="toggleAllVisible" />
              <span>Selecionar {{ query || onlyOnline ? 'os da lista' : 'todos' }}</span>
            </label>
            <span class="sa-count">{{ selected.size }} selecionado(s)</span>
          </div>

          <div class="sa-list">
            <div v-if="loading && !agents.length" class="sa-state"><span class="sa-spinner"></span> Carregando sessões...</div>
            <div v-else-if="!visible.length" class="sa-state">{{ agents.length ? 'Nenhum agente nesse filtro.' : 'Nenhum agente com sessão aberta.' }}</div>
            <label
              v-for="a in visible"
              v-else
              :key="a.agentId"
              class="sa-agent"
              :class="{ 'sa-agent--on': selected.has(a.agentId), 'sa-agent--locked': onlyMine(a) }"
              :title="onlyMine(a) ? 'Esta é a sua sessão em uso — ela não pode ser revogada por aqui' : ''"
            >
              <input type="checkbox" :checked="selected.has(a.agentId)" :disabled="onlyMine(a)" @change="toggle(a.agentId)" />
              <span class="sa-avatar">
                <img v-if="avatarSrc(a)" :src="avatarSrc(a)!" alt="" @error="brokenAvatars.add(a.agentId)" />
                <template v-else>{{ initials(a.name) }}</template>
                <span class="sa-dot" :class="'sa-dot--' + presence(a)"></span>
              </span>
              <span class="sa-agent-body">
                <span class="sa-agent-name">
                  {{ a.name }}
                  <span v-if="!a.isActive" class="sa-tag sa-tag--warn">desativado</span>
                </span>
                <span class="sa-agent-sub">{{ a.email }}</span>
                <span class="sa-sessions">
                  <span v-for="s in a.sessions" :key="s.id" class="sa-session" :class="{ 'sa-session--current': s.isCurrent, 'sa-session--hit': willRevoke(a, s) }">
                    {{ shortDevice(s.device) }} · {{ ago(s.lastSeenAt) }}
                    <em v-if="s.isMine">sua</em>
                    <em v-else-if="s.isCurrent">atual</em>
                  </span>
                </span>
              </span>
              <span class="sa-status" :class="'sa-status--' + presence(a)">{{ PRESENCE_LABEL[presence(a)] }}</span>
            </label>
          </div>

          <p v-if="error" class="sa-error">{{ error }}</p>
          <p v-if="done" class="sa-done">{{ done }}</p>

          <div class="sa-foot">
            <div class="sa-scope" role="radiogroup" aria-label="O que revogar">
              <button v-for="opt in SCOPES" :key="opt.value" type="button" role="radio" :aria-checked="scope === opt.value" :class="{ 'is-on': scope === opt.value }" @click="scope = opt.value">
                {{ opt.label }}
              </button>
            </div>
            <p class="sa-scope-hint">{{ SCOPES[scope].hint }}</p>

            <div v-if="!confirming" class="sa-actions">
              <span class="sa-impact">{{ impactLabel }}</span>
              <button type="button" class="sa-btn sa-btn--danger" :disabled="!impact.sessions || busy" @click="confirming = true">Revogar</button>
            </div>
            <div v-else class="sa-confirm">
              <span>
                Revogar <b>{{ impact.sessions }} {{ impact.sessions === 1 ? 'sessão' : 'sessões' }}</b> de <b>{{ impact.agents }} {{ impact.agents === 1 ? 'agente' : 'agentes' }}</b>?
                Eles veem "Esta sessão foi revogada pelo Sistema" e são desconectados.
              </span>
              <div class="sa-confirm-btns">
                <button type="button" class="sa-btn" :disabled="busy" @click="confirming = false">Cancelar</button>
                <button type="button" class="sa-btn sa-btn--danger" :disabled="busy" @click="revoke">{{ busy ? 'Revogando...' : 'Sim, revogar' }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
// Sessões dos agentes (Ctrl+Shift+S, permissão sessions.manage — perfil Sistema). Seleciona vários agentes e revoga a
// sessão atual (a de sinal mais recente), as demais abertas ou as duas. O backend avisa cada sessão pelo hub
// ("SessaoRevogada") e a aba do agente sai com o aviso (useSessionTracking.ts). A sua própria sessão nunca é revogada.
import { ref, reactive, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { toMediaUrl } from '@/utils/media'
import { useStore } from 'vuex'
import { listAgentSessions, revokeAgentSessions, type AgentSessions, type AgentSessionItem, type SessionRevokeScope } from '@/services/wootdashSessions'
import { sessionAdminOpen } from '@/composables/useSessionAdmin'

type Presence = 'online' | 'away' | 'offline'
const PRESENCE_LABEL: Record<Presence, string> = { online: 'Online', away: 'Ausente', offline: 'Offline' }
const SCOPES: { value: SessionRevokeScope; label: string; hint: string }[] = [
  { value: 0, label: 'Sessão atual', hint: 'Só a sessão em uso de cada agente (a de sinal mais recente). As outras abas e navegadores continuam.' },
  { value: 1, label: 'Sessões abertas', hint: 'As outras sessões de cada agente (abas e navegadores antigos). A sessão em uso continua.' },
  { value: 2, label: 'As duas', hint: 'Todas as sessões de cada agente. Ele sai do sistema e fica offline.' },
]

const store = useStore()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')

const agents = ref<AgentSessions[]>([])
const loading = ref(false)
const busy = ref(false)
const error = ref('')
const done = ref('')
const query = ref('')
const onlyOnline = ref(false)
const scope = ref<SessionRevokeScope>(0)
const confirming = ref(false)
const selected = ref(new Set<string>())
const searchRef = ref<HTMLInputElement | null>(null)
const nowTick = ref(Date.now())
let tickTimer: ReturnType<typeof setInterval> | undefined

const presence = (a: AgentSessions): Presence => {
  const s = a.status.toLowerCase()
  return s === 'online' ? 'online' : s === 'offline' ? 'offline' : 'away'
}
// avatarUrl vem cru da API (caminho relativo de POST /anexos) — toMediaUrl monta a URL exibível, como no resto do
// sistema. Foto que não carrega (arquivo removido) cai pras iniciais.
const brokenAvatars = reactive(new Set<string>())
const avatarSrc = (a: AgentSessions): string | null => (brokenAvatars.has(a.agentId) ? null : toMediaUrl(a.avatarUrl))
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?'
const onlyMine = (a: AgentSessions) => a.sessions.every(s => s.isMine)

const onlineCount = computed(() => agents.value.filter(a => presence(a) === 'online').length)
const visible = computed(() => {
  const q = query.value.trim().toLowerCase()
  return agents.value
    .filter(a => !onlyOnline.value || presence(a) === 'online')
    .filter(a => !q || a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q))
})
const selectable = computed(() => visible.value.filter(a => !onlyMine(a)))
const allVisibleSelected = computed(() => selectable.value.length > 0 && selectable.value.every(a => selected.value.has(a.agentId)))
const someVisibleSelected = computed(() => selectable.value.some(a => selected.value.has(a.agentId)))

/** Mesma regra do backend: atual = a 1ª (sinal mais recente); a sua nunca entra. */
function willRevoke(a: AgentSessions, s: AgentSessionItem): boolean {
  if (!selected.value.has(a.agentId) || s.isMine) return false
  return scope.value === 2 || (scope.value === 0 ? s.isCurrent : !s.isCurrent)
}
const impact = computed(() => {
  let sessions = 0
  let agentsHit = 0
  for (const a of agents.value) {
    const n = a.sessions.filter(s => willRevoke(a, s)).length
    sessions += n
    if (n) agentsHit++
  }
  return { sessions, agents: agentsHit }
})
const impactLabel = computed(() => {
  if (!selected.value.size) return 'Selecione os agentes na lista.'
  if (!impact.value.sessions) return 'Nenhuma sessão para revogar com essa opção.'
  return `${impact.value.sessions} ${impact.value.sessions === 1 ? 'sessão' : 'sessões'} de ${impact.value.agents} ${impact.value.agents === 1 ? 'agente' : 'agentes'}`
})

function ago(iso: string): string {
  const s = Math.max(0, Math.round((nowTick.value - new Date(iso).getTime()) / 1000))
  if (s < 60) return 'agora'
  const m = Math.round(s / 60)
  if (m < 60) return `há ${m} min`
  const h = Math.round(m / 60)
  return h < 24 ? `há ${h} h` : `há ${Math.round(h / 24)} d`
}

/** User-agent vira "Chrome · Windows"; rótulo curto passa como está. */
function shortDevice(device: string): string {
  if (!device) return 'Desconhecido'
  if (!/mozilla|applewebkit|gecko/i.test(device)) return device
  const browser = /edg\//i.test(device) ? 'Edge' : /opr\//i.test(device) ? 'Opera' : /firefox\//i.test(device) ? 'Firefox'
    : /chrome\//i.test(device) ? 'Chrome' : /safari\//i.test(device) ? 'Safari' : 'Navegador'
  const os = /windows/i.test(device) ? 'Windows' : /android/i.test(device) ? 'Android' : /iphone|ipad/i.test(device) ? 'iOS'
    : /mac os/i.test(device) ? 'macOS' : /linux/i.test(device) ? 'Linux' : ''
  return os ? `${browser} · ${os}` : browser
}

function toggle(id: string) {
  const next = new Set(selected.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selected.value = next
  confirming.value = false
}
function toggleAllVisible() {
  const next = new Set(selected.value)
  if (allVisibleSelected.value) selectable.value.forEach(a => next.delete(a.agentId))
  else selectable.value.forEach(a => next.add(a.agentId))
  selected.value = next
  confirming.value = false
}
watch(scope, () => { confirming.value = false })

async function load() {
  loading.value = true
  error.value = ''
  try {
    agents.value = await listAgentSessions(token.value)
    // Quem saiu da lista (sem sessão) sai da seleção.
    const ids = new Set(agents.value.map(a => a.agentId))
    selected.value = new Set([...selected.value].filter(id => ids.has(id)))
    nowTick.value = Date.now()
  } catch (e: any) {
    error.value = e?.message || 'Não deu pra carregar as sessões.'
  } finally {
    loading.value = false
  }
}

async function revoke() {
  if (busy.value) return
  busy.value = true
  error.value = ''
  done.value = ''
  try {
    const ids = [...selected.value].filter(id => agents.value.some(a => a.agentId === id && a.sessions.some(s => willRevoke(a, s))))
    const result = await revokeAgentSessions(token.value, ids, scope.value)
    done.value = result.revoked
      ? `${result.revoked} ${result.revoked === 1 ? 'sessão revogada' : 'sessões revogadas'} de ${result.agents.length} ${result.agents.length === 1 ? 'agente' : 'agentes'}.`
      : 'Nenhuma sessão foi revogada (as sessões já tinham sido encerradas).'
    selected.value = new Set()
    confirming.value = false
    await load()
  } catch (e: any) {
    error.value = e?.message || 'Não deu pra revogar as sessões.'
  } finally {
    busy.value = false
  }
}

function close() {
  sessionAdminOpen.value = false
}

watch(sessionAdminOpen, async open => {
  if (tickTimer) { clearInterval(tickTimer); tickTimer = undefined }
  if (!open) return
  query.value = ''
  error.value = ''
  done.value = ''
  confirming.value = false
  selected.value = new Set()
  tickTimer = setInterval(() => { nowTick.value = Date.now() }, 30_000)
  await nextTick()
  searchRef.value?.focus()
  await load()
})

function onKeydown(e: KeyboardEvent) {
  if (sessionAdminOpen.value && e.key === 'Escape') {
    if (confirming.value) confirming.value = false
    else close()
  }
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  if (tickTimer) clearInterval(tickTimer)
})
</script>

<style scoped>
.sa-overlay {
  position: fixed; inset: 0; z-index: 9100;
  background: rgba(0, 0, 0, 0.55); backdrop-filter: blur(4px);
  display: flex; align-items: flex-start; justify-content: center; padding: 7vh 16px 16px;
}
.sa-box {
  width: 100%; max-width: 640px; max-height: 86vh;
  background: #1a2332; color: #e5e7eb;
  border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 14px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
  display: flex; flex-direction: column; overflow: hidden;
}

.sa-head { display: flex; align-items: center; gap: 10px; padding: 14px 16px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
.sa-head-icon { width: 32px; height: 32px; border-radius: 9px; display: grid; place-items: center; background: rgba(239, 68, 68, 0.16); color: #fca5a5; flex: none; }
.sa-head-text { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.sa-head-text strong { font-size: 14px; color: #fff; }
.sa-head-text span { font-size: 12px; color: rgba(255, 255, 255, 0.55); }
.sa-kbd { font-size: 11px; padding: 2px 7px; border-radius: 5px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.12); color: rgba(255, 255, 255, 0.6); font-family: inherit; white-space: nowrap; }
.sa-icon-btn { width: 28px; height: 28px; border-radius: 7px; display: grid; place-items: center; background: transparent; border: 0; color: rgba(255, 255, 255, 0.6); cursor: pointer; flex: none; }
.sa-icon-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.08); color: #fff; }
.sa-icon-btn:disabled { opacity: 0.4; cursor: wait; }

.sa-search-row { display: flex; align-items: center; gap: 10px; padding: 10px 16px; color: rgba(255, 255, 255, 0.45); border-bottom: 1px solid rgba(255, 255, 255, 0.06); flex-wrap: wrap; }
.sa-search { flex: 1; min-width: 160px; background: transparent; border: 0; outline: none; color: #fff; font-size: 14px; font-family: inherit; }
.sa-search::placeholder { color: rgba(255, 255, 255, 0.35); }
.sa-filters { display: inline-flex; border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 8px; overflow: hidden; }
.sa-filters button { background: transparent; border: 0; color: rgba(255, 255, 255, 0.6); font-size: 12px; padding: 4px 10px; cursor: pointer; font-family: inherit; }
.sa-filters button b { font-weight: 600; color: rgba(255, 255, 255, 0.85); margin-left: 3px; }
.sa-filters button.is-on { background: rgba(255, 255, 255, 0.12); color: #fff; }

.sa-selectbar { display: flex; align-items: center; justify-content: space-between; padding: 8px 16px; border-bottom: 1px solid rgba(255, 255, 255, 0.06); font-size: 12.5px; color: rgba(255, 255, 255, 0.7); }
.sa-check { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; }
.sa-count { color: rgba(255, 255, 255, 0.5); }
input[type="checkbox"] { width: 15px; height: 15px; accent-color: #ef4444; cursor: pointer; flex: none; }

.sa-list { overflow-y: auto; padding: 6px; flex: 1; min-height: 140px; }
.sa-state { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 28px; font-size: 13px; color: rgba(255, 255, 255, 0.5); }
.sa-agent { display: flex; align-items: flex-start; gap: 11px; padding: 9px 10px; border-radius: 9px; cursor: pointer; border: 1px solid transparent; }
.sa-agent:hover { background: rgba(255, 255, 255, 0.05); }
.sa-agent--on { background: rgba(239, 68, 68, 0.09); border-color: rgba(239, 68, 68, 0.28); }
.sa-agent--locked { cursor: default; opacity: 0.6; }
.sa-agent > input { margin-top: 10px; }
.sa-avatar { position: relative; width: 34px; height: 34px; border-radius: 50%; background: #334155; display: grid; place-items: center; font-size: 12px; font-weight: 600; color: #fff; flex: none; }
.sa-avatar img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
.sa-dot { position: absolute; right: -1px; bottom: -1px; width: 10px; height: 10px; border-radius: 50%; border: 2px solid #1a2332; }
.sa-dot--online { background: #22c55e; }
.sa-dot--away { background: #f59e0b; }
.sa-dot--offline { background: #64748b; }
.sa-agent-body { display: flex; flex-direction: column; min-width: 0; flex: 1; gap: 1px; }
.sa-agent-name { font-size: 13.5px; font-weight: 600; color: #fff; display: flex; align-items: center; gap: 6px; }
.sa-agent-sub { font-size: 12px; color: rgba(255, 255, 255, 0.5); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sa-tag { font-size: 10.5px; font-weight: 600; padding: 0 6px; border-radius: 4px; }
.sa-tag--warn { background: rgba(245, 158, 11, 0.18); color: #fbbf24; }
.sa-sessions { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.sa-session { font-size: 11px; padding: 1px 7px; border-radius: 5px; background: rgba(255, 255, 255, 0.06); color: rgba(255, 255, 255, 0.65); border: 1px solid transparent; }
.sa-session em { font-style: normal; font-weight: 700; margin-left: 2px; color: #93c5fd; }
.sa-session--current { color: rgba(255, 255, 255, 0.85); }
.sa-session--hit { border-color: rgba(239, 68, 68, 0.55); color: #fecaca; text-decoration: line-through; text-decoration-color: rgba(239, 68, 68, 0.7); }
.sa-status { font-size: 11.5px; color: rgba(255, 255, 255, 0.4); margin-top: 8px; white-space: nowrap; }
.sa-status--online { color: #4ade80; }
.sa-status--away { color: #fbbf24; }

.sa-error { margin: 0; padding: 8px 16px; font-size: 12.5px; color: #fca5a5; background: rgba(239, 68, 68, 0.1); }
.sa-done { margin: 0; padding: 8px 16px; font-size: 12.5px; color: #86efac; background: rgba(34, 197, 94, 0.1); }

.sa-foot { padding: 12px 16px 14px; border-top: 1px solid rgba(255, 255, 255, 0.08); display: flex; flex-direction: column; gap: 8px; }
.sa-scope { display: grid; grid-template-columns: repeat(3, 1fr); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 9px; overflow: hidden; }
.sa-scope button { background: transparent; border: 0; border-right: 1px solid rgba(255, 255, 255, 0.12); color: rgba(255, 255, 255, 0.7); font-size: 12.5px; font-weight: 600; padding: 7px 6px; cursor: pointer; font-family: inherit; }
.sa-scope button:last-child { border-right: 0; }
.sa-scope button.is-on { background: rgba(239, 68, 68, 0.2); color: #fff; }
.sa-scope-hint { margin: 0; font-size: 11.5px; line-height: 1.5; color: rgba(255, 255, 255, 0.5); }
.sa-actions { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.sa-impact { font-size: 12.5px; color: rgba(255, 255, 255, 0.7); }
.sa-confirm { display: flex; flex-direction: column; gap: 10px; padding: 10px 12px; border-radius: 9px; background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); font-size: 12.5px; line-height: 1.5; color: #fecaca; }
.sa-confirm b { color: #fff; }
.sa-confirm-btns { display: flex; justify-content: flex-end; gap: 8px; }
.sa-btn { height: 32px; padding: 0 14px; border-radius: 8px; background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12); color: rgba(255, 255, 255, 0.85); font-size: 12.5px; font-weight: 600; cursor: pointer; font-family: inherit; }
.sa-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.12); }
.sa-btn:disabled { opacity: 0.45; cursor: default; }
.sa-btn--danger { background: #b91c1c; border-color: #b91c1c; color: #fff; }
.sa-btn--danger:hover:not(:disabled) { background: #991b1b; }

.sa-spinner { width: 14px; height: 14px; border-radius: 50%; border: 2px solid rgba(255, 255, 255, 0.2); border-top-color: #fca5a5; animation: sa-spin 0.8s linear infinite; display: inline-block; }
@keyframes sa-spin { to { transform: rotate(360deg); } }

.sa-fade-enter-active, .sa-fade-leave-active { transition: opacity 0.15s ease; }
.sa-fade-enter-from, .sa-fade-leave-to { opacity: 0; }

@media (max-width: 640px) {
  .sa-overlay { padding: 0; }
  .sa-box { max-width: none; height: 100vh; max-height: none; border-radius: 0; }
  .sa-kbd { display: none; }
}
</style>
